import { computed, inject, Injectable, Injector, runInInjectionContext, signal } from "@angular/core"
import { Auth, getIdTokenResult, User, UserInfo } from "@angular/fire/auth"
import { IdTokenResultWithClaims, UserRecord } from "./firebase-auth-state.model"
import {
  decodeCustomClaims,
  decodeRegionRoles,
  decodeRoles,
  encodeCustomClaims,
  RegionRoles,
  RegionsEnum,
  RolesEnum,
  EncodedRegionRoles
} from "redlineLibs"
import { map } from "rxjs"
import { HttpClient } from "@angular/common/http"
import { FirebaseAuthProfileService } from "./firebase-auth-profile.service"
import { ProviderType } from "util/auth-provider"
import { environment } from "../../environments/environment"

@Injectable({
  providedIn: "root",
})
export class FirebaseAuthService {
  private auth = inject(Auth)
  private profileDataAccessService = inject(FirebaseAuthProfileService)
  private httpClient = inject(HttpClient)
  private injector = inject(Injector)

  afUserDisplayName = computed(() => this.afUser()?.displayName)
  loginDetails = computed(() => {
    const afUser = this.afUser()
    const providerData = afUser?.providerData?.[0]
    let details = {
      name: "",
      email: "",
      provider: "",
    }
    switch (providerData?.providerId) {
      case "password":
        details = {
          name: "", // never populated when providerId is password
          email: afUser?.email || "", // assuming populated when providerId is password
          provider: "Username/Password",
        }
        break
      case "microsoft.com":
        details = {
          name: afUser?.providerData?.[0].displayName || "", // assuming populated when providerId is microsoft.com
          email: afUser?.providerData?.[0].email || "", // assuming populated when providerId is microsoft.com
          provider: "Microsoft",
        }
        break
      case "google.com":
        details = {
          name: afUser?.displayName || "", // assuming populated when providerId is google.com
          email: afUser?.email || "", // found one example where email in not provided
          provider: "Google",
        }
        break
      case "github.com":
        details = {
          name: afUser?.displayName || "", // assuming not provided by github
          email: afUser?.email || "", // assuming not provided by github
          provider: "Github",
        }
        break
    }
    return details
  })
  /**
   * we lost providerData from afUser, now providerId is always "firebase"
   */
  providerData = computed(() => {
    // console.log(this.afUser())
    const providerData = this.afUser()?.providerData?.[0]
    if (providerData) {
      return {
        ...providerData,
      }
    } else {
      return (this.afUser() || {}) as UserInfo
    }
  })
  providerId = computed(() => this.providerData().providerId as ProviderType | undefined)
  afUserEmail = computed(() => this.afUser()?.email || "")
  afUserEmailVerified = computed(() => !!this.afUser()?.emailVerified)
  // afUserPhoneNumber = computed(() => this.afUser()?.phoneNumber || "")
  afUserPhotoUrl = computed(() => this.afUser()?.photoURL || "")
  afUserUid = computed(() => this.afUser()?.uid)
  userInit = signal(false)
  userId = computed(() => this.afUserUid() || "")
  loggedIn = computed(() => this.afUser() && !this.afUser()?.isAnonymous)
  claims = computed(() => this.idTokenResultWithClaims()?.claims || {})
  // claimsUserId = computed(() => this.claims().userId || "")
  claimsRoles = computed(() => this.claims().roles || [])
  // afUserIsAnonymous = computed(() => !!this.afUser()?.isAnonymous)
  claimsRegionRoles = computed(() => this.claims().regionRoles || {} as RegionRoles)
  isAdmin = computed(() =>
    this.claimsRoles()?.includes(RolesEnum.ADMIN),
  )

  // profile = signal<Profile | undefined>(undefined)
  // claimsEmail = computed(() => this.claims().email || "")
  // claimsEmailVerified = computed(() => !!this.claims().emailVerified)
  // claimsName = computed(() => this.claims().name || "")
  // claimsPicture = computed(() => this.claims().picture || "")
  private baseUrl = environment.baseUrl + "/api-v1"
  private _afUser = signal<Partial<User> | null | undefined>(undefined)
  afUser = this._afUser.asReadonly()
  private _idTokenResultWithClaims = signal<IdTokenResultWithClaims | undefined>(undefined)
  idTokenResultWithClaims = this._idTokenResultWithClaims.asReadonly()

  constructor() {
    this.idTokenListener()
    this.authStateChangeListener()
  }

  /**
   * monitor user's auth state
   */
  authStateChangeListener() {
    this.auth.onAuthStateChanged((user) => {
      const userData: Partial<User> | null = user && user.toJSON() || null
      // console.log(userData)
      this.setAfUser(userData)
    })

  }

  /**
   * monitor user's auth status
   */
  idTokenListener() {
    this.auth.onIdTokenChanged((afUser) => {
      runInInjectionContext(this.injector, () => {
        this.userInit.set(true)

        if (!afUser || !afUser.uid) {
          this.profileDataAccessService.clearProfile()
          if (this.profileDataAccessService.userAccountUnsubscribe) {
            this.profileDataAccessService.userAccountUnsubscribe()
            this.profileDataAccessService.userAccountUnsubscribe = undefined
          }
          this._idTokenResultWithClaims.set(undefined)
        }
        if (afUser && afUser.uid) {
          this.profileDataAccessService.userAccountSubscribe(afUser)
          getIdTokenResult(afUser)
            .then(idTokenResult => {
              const claims = idTokenResult.claims as { roles?: unknown; regionRoles?: unknown }
              const decoded: IdTokenResultWithClaims = {
                ...idTokenResult,
                claims: {
                  ...idTokenResult.claims,
                  roles: decodeRoles(claims.roles),
                  regionRoles: decodeRegionRoles(claims.regionRoles as EncodedRegionRoles),
                },
              }
              this._idTokenResultWithClaims.set(decoded)
            })
            .catch(error => {
              this._idTokenResultWithClaims.set(undefined)
              console.log(error)
            })
        }
      })
    })
  }

  /**
   * one userRecord from Firebase Authentication
   * mapped in cloud function, provides limited data, does not emit on changes
   */
  getUserRecord_FirebaseAuth(userId: string) {
    const url = this.baseUrl + "/users/" + userId
    return this.httpClient
      .get<{ userRecord: UserRecord }>(url)
      .pipe(
        map(({ userRecord }) => this.decodeUserRecordClaims(userRecord)),
      )
  }

  edit_FirebaseAuth(userId: string, currentRegion: RegionsEnum, regionRoles: RegionRoles, globalRoles: RolesEnum[]) {
    const url = this.baseUrl + "/users/" + userId + "?region=" + encodeURIComponent(currentRegion)
    const encodedCustomClaims = encodeCustomClaims({ roles: globalRoles, regionRoles })
    this.httpClient
      .patch(url, encodedCustomClaims)
      .subscribe({
        next: response => {
          // handle success response
          console.log("HTTP Success", response)
        },
        error: err => {
          // handle error response
          console.log("HTTP Error", err)
        },
      })
  }

  setAfUser(afUser: Partial<User> | null) {
    this._afUser.set(afUser)
  }

  private decodeUserRecordClaims(userRecord: UserRecord): UserRecord {
    if (!userRecord.customClaims) return userRecord
    return {
      ...userRecord,
      customClaims: decodeCustomClaims(userRecord.customClaims),
    }
  }

}
